import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Folder,
  FolderInput,
  PencilLine,
  Plus,
  Trash2,
  UserRound,
  UserPlus,
} from 'lucide-react';
import B2BLayout from '../../../components/b2b/B2BLayout';
import IndividualTrialNotice from '../../../components/IndividualTrialNotice';
import B2BPageShell, { B2BPageFooterAction } from '../../../components/b2b/B2BPageShell';
import { useAppContext } from '../../../context/AppContext';
import { Toast } from '../../../components/ui';
import {
  ErrorBanner,
  getPersonaInitial,
  PersonaAvatar,
  PersonaTagChip,
} from '../components/PersonaShared';
import { personaApi, personaAssetUrl, isDefaultPersonaFolder, resolvePersonaApiErrorMessage, sortPersonaFolders } from '../services/personaService';
import '../styles/persona.scss';

const PERSONA_FETCH_LIMIT = 100;
const LIST_PAGE_SIZE = 10;
const MOVE_TARGET_UNGROUPED = '__ungrouped__';
const FOLDER_NAME_MAX_LENGTH = 20;

const getPersonaProfile = (persona) => {
  const profile = persona?.profile || {};
  return {
    gender: persona?.gender || profile.gender || '성별 미상',
    age: persona?.age || profile.age,
    job: persona?.title || profile.job || profile.occupation || persona?.occupation || '직업 미상',
    personality:
      persona?.personality ||
      profile.personality ||
      persona?.personality_description ||
      '성격 정보가 아직 등록되지 않았습니다.',
    behavior:
      persona?.behaviours ||
      profile.behaviours ||
      persona?.behavior_pattern ||
      persona?.behavior_patterns ||
      profile.behavior_pattern ||
      profile.behavior_patterns ||
      persona?.lifestyle ||
      '행동 패턴 정보가 아직 등록되지 않았습니다.',
  };
};

const formatCompactDate = (value) => {
  if (!value) return 'YYYY.MM.DD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'YYYY.MM.DD';
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).replace(/\.\s?/g, '.').replace(/\.$/, '');
};

const formatFolderPersonaCount = (count) => (
  count >= 4 ? `총 퍼소나 ${count}` : `총 ${count} 퍼소나`
);

const getPersonaFolderId = (persona) => persona?.folder_id || persona?.folderId || persona?.folder?.id || '';

const getPersonaUpdatedTime = (persona) => {
  const value =
    persona?.updated_at ||
    persona?.updatedAt ||
    persona?.modified_at ||
    persona?.modifiedAt ||
    persona?.created_at ||
    persona?.createdAt ||
    '';
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const getTimeValue = (value) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const getFolderEmbeddedPersonas = (folder) => {
  const candidates = [
    folder?.recent_personas,
    folder?.recentPersonas,
    folder?.personas,
    folder?.items,
  ];
  return candidates.find(Array.isArray) || [];
};

const getRecentFolderPersonas = (folder, allPersonas) => {
  const embeddedPersonas = getFolderEmbeddedPersonas(folder);
  const sourcePersonas = embeddedPersonas.length > 0
    ? embeddedPersonas
    : allPersonas.filter((persona) => String(getPersonaFolderId(persona)) === String(folder.id));

  return [...sourcePersonas]
    .sort((a, b) => getPersonaUpdatedTime(b) - getPersonaUpdatedTime(a))
    .slice(0, 3);
};

const getFolderUpdatedAt = (folder, allPersonas, activityTimes) => {
  const folderId = String(folder?.id || '');
  const folderPersonas = getFolderEmbeddedPersonas(folder);
  const matchingPersonas = allPersonas.filter((persona) => (
    String(getPersonaFolderId(persona)) === folderId
  ));
  const personaUpdatedTimes = [...folderPersonas, ...matchingPersonas].map(getPersonaUpdatedTime);
  const folderUpdatedTimes = [
    activityTimes[folderId],
    folder?.updated_at,
    folder?.updatedAt,
    folder?.modified_at,
    folder?.modifiedAt,
    folder?.last_modified_at,
    folder?.lastModifiedAt,
    folder?.created_at,
    folder?.createdAt,
  ].map(getTimeValue);
  const latestTime = Math.max(0, ...folderUpdatedTimes, ...personaUpdatedTimes);
  return latestTime > 0 ? latestTime : null;
};

const getPersonaImageUrl = (persona) => personaAssetUrl(persona?.image_url || persona?.imageUrl);

async function listAllPersonas(params = {}) {
  const firstPage = await personaApi.listPersonas({
    ...params,
    page: 1,
    limit: PERSONA_FETCH_LIMIT,
  });
  const totalPages = firstPage.pagination?.total_pages || firstPage.pagination?.totalPages || 1;
  if (totalPages <= 1) return firstPage;

  const restPages = await Promise.all(
    Array.from({ length: totalPages - 1 }).map((_, index) => (
      personaApi.listPersonas({
        ...params,
        page: index + 2,
        limit: PERSONA_FETCH_LIMIT,
      })
    )),
  );

  return {
    ...firstPage,
    items: [
      ...(firstPage.items || []),
      ...restPages.flatMap((page) => page.items || []),
    ],
  };
}

function PersonaListPage() {
  const { isEnterprise } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [folders, setFolders] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [ungroupedPersonaTotal, setUngroupedPersonaTotal] = useState(0);
  const [activeFolder, setActiveFolder] = useState(location.state?.activeFolder || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [folderName, setFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPersonas, setSelectedPersonas] = useState(() => new Set());
  const [selectedFolders, setSelectedFolders] = useState(() => new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingSelection, setDeletingSelection] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [movingSelection, setMovingSelection] = useState(false);
  const [selectedMoveTarget, setSelectedMoveTarget] = useState('');
  const [listPage, setListPage] = useState(1);
  const [folderNameError, setFolderNameError] = useState('');
  const [folderEditName, setFolderEditName] = useState('');
  const [updatingFolderName, setUpdatingFolderName] = useState(false);
  const [folderActivityTimes, setFolderActivityTimes] = useState({});
  const [toast, setToast] = useState({ isOpen: false, type: 'success', message: '' });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ isOpen: true, type, message });
  }, []);

  const touchFolderActivityTimes = useCallback((folderIds) => {
    const keys = Array.from(new Set(
      Array.from(folderIds || [])
        .filter((folderId) => folderId !== null && folderId !== undefined && folderId !== '')
        .map(String),
    ));
    if (keys.length === 0) return;
    const updatedAt = new Date().toISOString();
    setFolderActivityTimes((current) => (
      keys.reduce((next, folderId) => ({ ...next, [folderId]: updatedAt }), { ...current })
    ));
  }, []);

  useEffect(() => {
    if (!location.state?.toast?.message) return;
    showToast(location.state.toast.message, location.state.toast.type || 'success');
    navigate(location.pathname, {
      replace: true,
      state: location.state.activeFolder ? { activeFolder: location.state.activeFolder } : null,
    });
  }, [location.pathname, location.state, navigate, showToast]);

  useEffect(() => {
    const folderFromState = location.state?.activeFolder;
    if (folderFromState !== undefined && folderFromState !== null && folderFromState !== '') {
      setActiveFolder(String(folderFromState));
    }
  }, [location.state?.activeFolder]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [folderData, personaData, ungroupedData] = await Promise.all([
        personaApi.listFolders(),
        listAllPersonas({
          ...(activeFolder ? { folder_id: activeFolder } : { no_folder: true }),
          exclude_source_type: 'explore',
        }),
        activeFolder
          ? personaApi.listPersonas({ page: 1, limit: 1, no_folder: true, exclude_source_type: 'explore' }).catch(() => null)
          : Promise.resolve(null),
      ]);
      const normalizedFolders = Array.isArray(folderData) ? folderData : [];
      const foldersWithRecentPersonas = await Promise.all(normalizedFolders.map(async (folder) => {
        if (!activeFolder) {
          if (getFolderEmbeddedPersonas(folder).length > 0) return folder;
          try {
            const folderPersonas = await personaApi.listPersonas({
              page: 1,
              limit: 3,
              folder_id: folder.id,
              exclude_source_type: 'explore',
            });
            return {
              ...folder,
              recentPersonas: folderPersonas.items,
              persona_count: folderPersonas.pagination?.total ?? folderPersonas.items.length,
            };
          } catch {
            return folder;
          }
        }

        if (
          folder.persona_count != null ||
          folder.personas_count != null ||
          folder.count != null
        ) {
          return folder;
        }

        try {
          const folderPersonas = await personaApi.listPersonas({
            page: 1,
            limit: 1,
            folder_id: folder.id,
            exclude_source_type: 'explore',
          });
          return {
            ...folder,
            persona_count: folderPersonas.pagination?.total ?? folderPersonas.items.length,
          };
        } catch {
          return folder;
        }
      }));

      setFolders(foldersWithRecentPersonas);
      setPersonas(personaData.items);
      setPagination(personaData.pagination);
      setUngroupedPersonaTotal(
        activeFolder
          ? (ungroupedData?.pagination?.total ?? ungroupedData?.items?.length ?? 0)
          : (personaData.pagination?.total ?? personaData.items.length),
      );
    } catch (err) {
      setError(resolvePersonaApiErrorMessage(err, 'Persona 데이터를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [activeFolder]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSelectedPersonas(new Set());
    setSelectedFolders(new Set());
    setShowDeleteModal(false);
    setShowMoveModal(false);
    setSelectedMoveTarget('');
  }, [editMode, activeFolder]);

  useEffect(() => {
    setListPage(1);
  }, [activeFolder]);

  const hasItems = folders.length > 0 || personas.length > 0;
  const hasVisibleItems = activeFolder ? personas.length > 0 : hasItems;

  const activeFolderLabel = useMemo(() => {
    if (!activeFolder) return '';
    return folders.find((folder) => String(folder.id) === String(activeFolder))?.name || '선택한 폴더';
  }, [activeFolder, folders]);
  const activeFolderRecord = useMemo(() => {
    if (!activeFolder) return null;
    return folders.find((folder) => String(folder.id) === String(activeFolder)) || null;
  }, [activeFolder, folders]);
  const activeFolderIsDefault = isDefaultPersonaFolder(activeFolderRecord);

  useEffect(() => {
    if (activeFolderIsDefault && editMode) {
      setEditMode(false);
    }
  }, [activeFolderIsDefault, editMode]);

  useEffect(() => {
    setFolderEditName(activeFolderLabel);
  }, [activeFolderLabel]);

  const folderCards = useMemo(() => sortPersonaFolders(folders.map((folder) => {
    const folderId = String(folder.id);
    const embeddedPersonas = getFolderEmbeddedPersonas(folder);
    const count =
      folder.persona_count ??
      folder.personas_count ??
      folder.count ??
      folder.recentPersonasTotal ??
      (embeddedPersonas.length > 0 ? embeddedPersonas.length : undefined) ??
      personas.filter((persona) => String(getPersonaFolderId(persona)) === folderId).length;

    return {
      ...folder,
      personaCount: count,
      recentPersonas: getRecentFolderPersonas(folder, personas),
      updatedAt: getFolderUpdatedAt(folder, personas, folderActivityTimes),
    };
  })), [folders, folderActivityTimes, personas]);

  const totalCount = activeFolder
    ? (pagination?.total ?? personas.length)
    : personas.length + folderCards.reduce((sum, folder) => sum + (Number(folder.personaCount) || 0), 0);

  const listItems = useMemo(() => {
    const folderItems = !activeFolder
      ? folderCards.map((folder) => ({ type: 'folder', key: `folder-${folder.id}`, data: folder }))
      : [];
    const personaItems = personas.map((persona) => ({ type: 'persona', key: `persona-${persona.id}`, data: persona }));
    return [...folderItems, ...personaItems];
  }, [activeFolder, folderCards, personas]);
  const listPageCount = Math.max(1, Math.ceil(listItems.length / LIST_PAGE_SIZE));
  const normalizedListPage = Math.min(listPage, listPageCount);
  const pagedListItems = listItems.slice(
    (normalizedListPage - 1) * LIST_PAGE_SIZE,
    normalizedListPage * LIST_PAGE_SIZE,
  );
  const selectableVisibleFolderIds = pagedListItems
    .filter((item) => item.type === 'folder' && !isDefaultPersonaFolder(item.data))
    .map((item) => String(item.data.id));
  const visiblePersonaIds = pagedListItems
    .filter((item) => item.type === 'persona')
    .map((item) => String(item.data.id));
  const selectableVisiblePersonaIds = activeFolderIsDefault
    ? []
    : visiblePersonaIds;
  const selectedCount = selectedPersonas.size + selectedFolders.size;
  const selectedPersonaCount = selectedPersonas.size;
  const selectedFolderCount = selectedFolders.size;
  const canMoveSelection = selectedPersonaCount > 0 && selectedFolderCount === 0 && !activeFolderIsDefault;
  const visibleItemCount = selectableVisibleFolderIds.length + selectableVisiblePersonaIds.length;
  const allVisibleSelected = visibleItemCount > 0 &&
    selectableVisibleFolderIds.every((folderId) => selectedFolders.has(folderId)) &&
    selectableVisiblePersonaIds.every((personaId) => selectedPersonas.has(personaId));

  useEffect(() => {
    if (listPage > listPageCount) {
      setListPage(listPageCount);
    }
  }, [listPage, listPageCount]);
  const selectedPersonaItems = useMemo(
    () => personas.filter((persona) => selectedPersonas.has(String(persona.id))),
    [personas, selectedPersonas],
  );
  const selectedCurrentFolderKeys = useMemo(() => {
    const keys = new Set();
    selectedPersonaItems.forEach((persona) => {
      const folderId = getPersonaFolderId(persona);
      keys.add(folderId ? String(folderId) : MOVE_TARGET_UNGROUPED);
    });
    return keys;
  }, [selectedPersonaItems]);
  const currentMoveTargetKey = selectedCurrentFolderKeys.size === 1
    ? Array.from(selectedCurrentFolderKeys)[0]
    : '';
  const moveTargets = useMemo(() => {
    const folderTargets = folderCards.map((folder) => {
      const key = String(folder.id);
      return {
        key,
        folderId: folder.id,
        name: folder.name || '폴더명',
        count: folder.personaCount ?? 0,
        type: 'folder',
        disabled: currentMoveTargetKey === key || isDefaultPersonaFolder(folder),
      };
    });

    return [
      ...folderTargets,
      {
        key: MOVE_TARGET_UNGROUPED,
        folderId: null,
        name: '그룹없음',
        count: ungroupedPersonaTotal,
        type: 'ungrouped',
        disabled: currentMoveTargetKey === MOVE_TARGET_UNGROUPED,
      },
    ];
  }, [currentMoveTargetKey, folderCards, ungroupedPersonaTotal]);
  const deleteModalCopy = useMemo(() => {
    if (selectedFolderCount > 0 && selectedPersonaCount > 0) {
      return {
        title: '선택한 항목을 삭제하시겠습니까?',
        description: '삭제 시 선택한 폴더와 퍼소나 정보는 모두 삭제되며',
      };
    }
    if (selectedFolderCount > 0) {
      return {
        title: '선택한 폴더를 삭제하시겠습니까?',
        description: '삭제 시 폴더와 폴더 안의 퍼소나 정보는 모두 삭제되며',
      };
    }
    return {
      title: '선택한 퍼소나를 삭제하시겠습니까?',
      description: '삭제 시 관련 해당 퍼소나의 정보는 모두 삭제되며',
    };
  }, [selectedFolderCount, selectedPersonaCount]);

  const handleCreateFolder = async (event) => {
    event.preventDefault();
    const name = folderName.trim();
    if (!name) return;
    if (name.length > FOLDER_NAME_MAX_LENGTH) {
      setFolderNameError(`폴더명은 최대 ${FOLDER_NAME_MAX_LENGTH}자까지 입력할 수 있습니다.`);
      return;
    }
    if (folders.some((folder) => String(folder.name || '').trim() === name)) {
      setFolderNameError('이미 존재하는 폴더명입니다.');
      return;
    }
    setCreatingFolder(true);
    setError('');
    setFolderNameError('');
    try {
      await personaApi.createFolder({ name, color: '#1f2023' });
      setFolderName('');
      setShowFolderModal(false);
      await loadData();
      showToast(`'${name}' 폴더를 생성했습니다.`);
    } catch (err) {
      setError(err.message || '폴더를 만들지 못했습니다.');
    } finally {
      setCreatingFolder(false);
    }
  };

  const saveFolderEditName = async () => {
    const name = folderEditName.trim();
    if (!activeFolder) return true;
    if (activeFolderIsDefault) {
      setError('');
      return true;
    }
    if (!name) {
      setError('폴더명을 입력해주세요.');
      return false;
    }
    if (name.length > FOLDER_NAME_MAX_LENGTH) {
      setError(`폴더명은 최대 ${FOLDER_NAME_MAX_LENGTH}자까지 입력할 수 있습니다.`);
      return false;
    }
    if (folders.some((folder) => String(folder.id) !== String(activeFolder) && String(folder.name || '').trim() === name)) {
      setError('이미 존재하는 폴더명입니다.');
      return false;
    }
    if (name === String(activeFolderLabel || '').trim()) {
      setError('');
      return true;
    }
    setUpdatingFolderName(true);
    setError('');
    try {
      await personaApi.updateFolder(activeFolder, { name });
      touchFolderActivityTimes([activeFolder]);
      await loadData();
      showToast(`'${name}' 폴더명이 수정되었습니다.`);
      return true;
    } catch (err) {
      setError(err.message || '폴더명을 수정하지 못했습니다.');
      return false;
    } finally {
      setUpdatingFolderName(false);
    }
  };

  const handleCompleteEditMode = async () => {
    if (updatingFolderName) return;
    const saved = await saveFolderEditName();
    if (saved) setEditMode(false);
  };

  const toggleSelectPersona = (personaId) => {
    if (activeFolderIsDefault) return;
    setSelectedPersonas((current) => {
      const next = new Set(current);
      const key = String(personaId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectFolder = (folderId) => {
    const folder = folderCards.find((item) => String(item.id) === String(folderId));
    if (isDefaultPersonaFolder(folder)) return;
    setSelectedFolders((current) => {
      const next = new Set(current);
      const key = String(folderId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const shouldClear = allVisibleSelected;
    setSelectedPersonas((current) => {
      const next = new Set(current);
      selectableVisiblePersonaIds.forEach((personaId) => {
        if (shouldClear) next.delete(personaId);
        else next.add(personaId);
      });
      return next;
    });
    setSelectedFolders((current) => {
      const next = new Set(current);
      selectableVisibleFolderIds.forEach((folderId) => {
        if (shouldClear) next.delete(folderId);
        else next.add(folderId);
      });
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (selectedCount === 0) return;
    setShowDeleteModal(true);
  };

  const handleOpenMoveModal = () => {
    if (!canMoveSelection) return;
    setSelectedMoveTarget('');
    setShowMoveModal(true);
  };

  const handleConfirmDeleteSelected = async () => {
    if (selectedCount === 0 || deletingSelection) return;
    const affectedFolderIds = selectedPersonaItems
      .map(getPersonaFolderId)
      .filter(Boolean);
    const successMessage = selectedFolderCount > 0 && selectedPersonaCount > 0
      ? '선택한 항목을 삭제했습니다.'
      : selectedFolderCount > 0
        ? '선택한 항목을 삭제했습니다.'
        : '선택한 퍼소나를 삭제했습니다.';
    setDeletingSelection(true);
    setError('');
    try {
      await Promise.all([
        ...Array.from(selectedPersonas).map((personaId) => personaApi.deletePersona(personaId)),
        ...Array.from(selectedFolders).map((folderId) => personaApi.deleteFolder(folderId)),
      ]);
      setSelectedPersonas(new Set());
      setSelectedFolders(new Set());
      setShowDeleteModal(false);
      touchFolderActivityTimes(affectedFolderIds);
      await loadData();
      showToast(successMessage);
    } catch (err) {
      setError(err.message || '선택한 항목을 삭제하지 못했습니다.');
    } finally {
      setDeletingSelection(false);
    }
  };

  const handleConfirmMoveSelected = async () => {
    if (!canMoveSelection || !selectedMoveTarget || movingSelection) return;
    const targetFolderId = selectedMoveTarget === MOVE_TARGET_UNGROUPED
      ? null
      : Number.isNaN(Number(selectedMoveTarget))
        ? selectedMoveTarget
        : Number(selectedMoveTarget);
    const sourceFolderIds = selectedPersonaItems
      .map(getPersonaFolderId)
      .filter(Boolean);
    const affectedFolderIds = [
      ...sourceFolderIds,
      ...(targetFolderId ? [targetFolderId] : []),
    ];

    setMovingSelection(true);
    setError('');
    try {
      const targetName = moveTargets.find((target) => target.key === selectedMoveTarget)?.name || '선택한 폴더';
      await Promise.all(
        Array.from(selectedPersonas).map((personaId) => (
          personaApi.updatePersona(personaId, { folder_id: targetFolderId })
        )),
      );
      setSelectedPersonas(new Set());
      setShowMoveModal(false);
      setSelectedMoveTarget('');
      touchFolderActivityTimes(affectedFolderIds);
      await loadData();
      showToast(`선택한 퍼소나를 '${targetName}'으로 이동했습니다.`);
    } catch (err) {
      setError(err.message || '선택한 항목을 이동하지 못했습니다.');
    } finally {
      setMovingSelection(false);
    }
  };

  const renderPersonaCard = (persona) => {
    const profile = getPersonaProfile(persona);
    const ageLabel = profile.age ? `${profile.age}세` : '연령 미상';
    const metaLabel = [profile.gender, ageLabel, profile.job].filter(Boolean).join(' · ');
    const selected = selectedPersonas.has(String(persona.id));
    const disabled = editMode && activeFolderIsDefault;

    return (
      <article
        key={persona.id}
        className={[
          'persona-list-card',
          editMode ? 'persona-list-card--editable' : '',
          selected ? 'persona-list-card--selected' : '',
          disabled ? 'persona-list-card--disabled' : '',
        ].filter(Boolean).join(' ')}
      >
        {editMode ? (
          <label className="persona-check persona-list-card__check" aria-label={`${persona.name} 선택`}>
            <input
              type="checkbox"
              checked={selected}
              disabled={disabled}
              onChange={() => toggleSelectPersona(persona.id)}
            />
            <span />
          </label>
        ) : null}

        <button
          type="button"
          className="persona-list-card__content"
          disabled={disabled}
          onClick={() => {
            if (editMode) {
              if (disabled) return;
              toggleSelectPersona(persona.id);
              return;
            }
            navigate(`/personas/${persona.id}`, {
              state: {
                from: activeFolder
                  ? { pathname: '/personas', activeFolder }
                  : { pathname: '/personas' },
              },
            });
          }}
        >
          <div className="persona-list-card__top">
            <PersonaAvatar persona={persona} size="sm" />
            <div className="persona-list-card__identity">
              <div className="persona-list-card__identity-main">
                <strong>{persona.name || '이름 없는 퍼소나'}</strong>
                <PersonaTagChip tag={persona.tag} />
              </div>
              <p className="persona-list-card__meta">{metaLabel}</p>
            </div>
            {!editMode ? <ChevronRight size={18} strokeWidth={1.8} /> : null}
          </div>
          <div className="persona-list-card__divider" />
          <PersonaCardSection title="성격" body={profile.personality} />
          <PersonaCardSection title="행동 패턴" body={profile.behavior} />
        </button>
      </article>
    );
  };

  return (
    <B2BLayout hideCollapseButton={true} defaultCollapsed={false} hidePageHeader={true}>
      <B2BPageShell
        className="persona-list-screen"
        title={isEnterprise ? 'Persona' : 'Generate'}
        description="AI 퍼소나를 생성하고, 다양한 테스트를 통해 제품 인사이트를 얻으세요"
        contentClassName="persona-list-content"
        footer={
          !editMode && !activeFolderIsDefault ? (
            <B2BPageFooterAction
              leftIcon={<Plus size={24} strokeWidth={1.8} />}
              onClick={() => navigate('/personas/new', {
                state: activeFolder ? { activeFolder } : undefined,
              })}
            >
              새 퍼소나 생성
            </B2BPageFooterAction>
          ) : null
        }
      >
        <ErrorBanner message={error} />
        <IndividualTrialNotice />

        {editMode ? (
          <>
            {activeFolderLabel ? (
              <FolderEditHeader
                value={folderEditName}
                count={totalCount}
                saving={updatingFolderName}
                disabled={activeFolderIsDefault}
                onChange={setFolderEditName}
                onBack={() => setActiveFolder('')}
              />
            ) : null}
            <EditToolbar
              selectedCount={selectedCount}
              canMove={canMoveSelection}
              allSelected={allVisibleSelected}
              doneDisabled={updatingFolderName}
              doneLabel={updatingFolderName ? '저장 중' : '편집 완료'}
              onSelectAll={toggleSelectAll}
              onDelete={handleDeleteSelected}
              onMove={handleOpenMoveModal}
              onDone={handleCompleteEditMode}
            />
          </>
        ) : activeFolderLabel ? (
          <FolderDetailHeader
            folderName={activeFolderLabel}
            totalCount={totalCount}
            showEdit={!activeFolderIsDefault}
            disabled={loading || personas.length === 0}
            onBack={() => setActiveFolder('')}
            onEdit={() => setEditMode(true)}
          />
        ) : (
          <ListHeader
            totalCount={totalCount}
            createDisabled={loading}
            editDisabled={!hasVisibleItems || loading}
            onCreateFolder={() => setShowFolderModal(true)}
            onEdit={() => setEditMode(true)}
          />
        )}

        {loading ? (
          <div className="persona-list-loading">
            <div className="persona-spinner" />
            <p>퍼소나 목록을 불러오는 중입니다.</p>
          </div>
        ) : !hasVisibleItems ? (
          <div className="persona-list-empty">
            <UserPlus size={38} strokeWidth={1.6} />
            <h2>등록된 퍼소나가 없습니다.</h2>
            <p>
              {activeFolderIsDefault
                ? '표시할 퍼소나가 없습니다.'
                : '하단의 버튼을 눌러 새로운 AI 퍼소나를 생성해 주세요.'}
            </p>
            {activeFolder ? (
              <button type="button" className="persona-secondary-button" onClick={() => setActiveFolder('')}>
                폴더 목록으로
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <div className="persona-list-grid">
              {pagedListItems.map((item) => (
                item.type === 'folder' ? (
                  <FolderCard
                    key={item.key}
                    folder={item.data}
                    editMode={editMode}
                    selected={selectedFolders.has(String(item.data.id))}
                    disabled={isDefaultPersonaFolder(item.data)}
                    onToggle={() => toggleSelectFolder(item.data.id)}
                    onClick={() => setActiveFolder(item.data.id)}
                  />
                ) : renderPersonaCard(item.data)
              ))}
            </div>
            {listPageCount > 1 ? (
              <ListPagination
                page={normalizedListPage}
                totalPages={listPageCount}
                onChange={setListPage}
              />
            ) : null}
          </>
        )}
      </B2BPageShell>

      {showFolderModal ? (
        <FolderModal
          value={folderName}
          creating={creatingFolder}
          error={folderNameError}
          onChange={setFolderName}
          onSubmit={handleCreateFolder}
          onCancel={() => {
            if (creatingFolder) return;
            setFolderName('');
            setFolderNameError('');
            setShowFolderModal(false);
          }}
        />
      ) : null}

      {showDeleteModal ? (
        <DeleteSelectionModal
          title={deleteModalCopy.title}
          description={deleteModalCopy.description}
          deleting={deletingSelection}
          onCancel={() => {
            if (deletingSelection) return;
            setShowDeleteModal(false);
          }}
          onConfirm={handleConfirmDeleteSelected}
        />
      ) : null}

      {showMoveModal ? (
        <MoveSelectionModal
          targets={moveTargets}
          selectedTarget={selectedMoveTarget}
          moving={movingSelection}
          onSelect={setSelectedMoveTarget}
          onCancel={() => {
            if (movingSelection) return;
            setShowMoveModal(false);
            setSelectedMoveTarget('');
          }}
          onConfirm={handleConfirmMoveSelected}
        />
      ) : null}
      <Toast
        isOpen={toast.isOpen}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((current) => ({ ...current, isOpen: false }))}
      />
    </B2BLayout>
  );
}

function ListHeader({ totalCount, createDisabled, editDisabled, onCreateFolder, onEdit }) {
  return (
    <div className="persona-list-header">
      <p>총 퍼소나 {totalCount}</p>
      <div>
        <button type="button" disabled={createDisabled} onClick={onCreateFolder}>
          <Plus size={18} strokeWidth={1.8} />
          새 폴더 생성
        </button>
        <button type="button" disabled={editDisabled} onClick={onEdit}>
          <PencilLine size={18} strokeWidth={1.8} />
          목록 편집
        </button>
      </div>
    </div>
  );
}

function FolderDetailHeader({ folderName, totalCount, showEdit = true, disabled, onBack, onEdit }) {
  return (
    <div className="persona-folder-detail-header">
      <div className="persona-folder-detail-header__title">
        <button type="button" aria-label="전체 폴더로 돌아가기" onClick={onBack}>
          <ChevronLeft size={16} strokeWidth={1.8} />
        </button>
        <strong>{folderName}</strong>
        <span>(총 퍼소나 {totalCount})</span>
      </div>
      {showEdit ? (
        <button
          type="button"
          className="persona-folder-detail-header__edit"
          disabled={disabled}
          onClick={onEdit}
        >
          <PencilLine size={18} strokeWidth={1.7} />
          목록 편집
        </button>
      ) : null}
    </div>
  );
}

function FolderEditHeader({ value, count, saving, disabled, onChange, onBack }) {
  return (
    <div className="persona-folder-edit-header">
      <button type="button" aria-label="전체 폴더로 돌아가기" onClick={onBack} disabled={saving}>
        <ChevronLeft size={16} strokeWidth={1.8} />
      </button>
      <input
        value={value}
        disabled={disabled || saving}
        maxLength={FOLDER_NAME_MAX_LENGTH}
        onChange={(event) => onChange(event.target.value.slice(0, FOLDER_NAME_MAX_LENGTH))}
        placeholder="폴더명을 입력해주세요"
      />
      <span>총 {count} 퍼소나</span>
    </div>
  );
}

function getPaginationItems(page, totalPages) {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }).map((_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, page, page - 1, page + 1]);
  const orderedPages = Array.from(pages)
    .filter((item) => item >= 1 && item <= totalPages)
    .sort((a, b) => a - b);

  return orderedPages.reduce((items, item, index) => {
    if (index > 0 && item - orderedPages[index - 1] > 1) {
      items.push(`ellipsis-${orderedPages[index - 1]}-${item}`);
    }
    items.push(item);
    return items;
  }, []);
}

function ListPagination({ page, totalPages, onChange }) {
  const items = getPaginationItems(page, totalPages);

  return (
    <nav className="persona-list-pagination" aria-label="퍼소나 목록 페이지">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        aria-label="이전 페이지"
      >
        <ChevronLeft size={16} />
      </button>
      <div>
        {items.map((item) => (
          typeof item === 'number' ? (
            <button
              key={item}
              type="button"
              className={item === page ? 'active' : ''}
              onClick={() => onChange(item)}
              aria-current={item === page ? 'page' : undefined}
            >
              {item}
            </button>
          ) : (
            <span key={item}>...</span>
          )
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        aria-label="다음 페이지"
      >
        <ChevronRight size={16} />
      </button>
    </nav>
  );
}

function EditToolbar({
  selectedCount,
  canMove,
  allSelected,
  doneDisabled = false,
  doneLabel = '편집 완료',
  onSelectAll,
  onDelete,
  onMove,
  onDone,
}) {
  const hasSelection = selectedCount > 0;

  return (
    <div className="persona-edit-toolbar">
      <label className="persona-edit-toolbar__select">
        <span className="persona-check">
          <input type="checkbox" checked={allSelected} onChange={onSelectAll} />
          <span />
        </span>
        전체선택
      </label>
      <div>
        <button type="button" disabled={!hasSelection} onClick={onDelete}>
          <Trash2 size={18} />
          선택항목 삭제
        </button>
        <button type="button" disabled={!canMove} onClick={onMove}>
          <FolderInput size={18} />
          선택항목 이동
        </button>
        <button type="button" className="persona-edit-toolbar__done" disabled={doneDisabled} onClick={onDone}>
          {doneLabel}
        </button>
      </div>
    </div>
  );
}

function FolderCard({ folder, editMode, selected, disabled, onToggle, onClick }) {
  const personaCount = folder.personaCount ?? 0;
  const avatarPersonas = Array.isArray(folder.recentPersonas) ? folder.recentPersonas.slice(0, 3) : [];
  const showFooterDate = Boolean(folder.updatedAt);

  return (
    <article
      className={[
        'persona-folder-card',
        editMode ? 'persona-folder-card--editable' : '',
        editMode && disabled ? 'persona-folder-card--disabled' : '',
        selected ? 'persona-folder-card--selected' : '',
      ].filter(Boolean).join(' ')}
    >
      {editMode ? (
        <label className="persona-check persona-folder-card__check" aria-label={`${folder.name || '폴더'} 선택`}>
          <input
            type="checkbox"
            checked={selected}
            disabled={disabled}
            onChange={onToggle}
          />
          <span />
        </label>
      ) : null}
      <button
        type="button"
        disabled={editMode && disabled}
        onClick={() => {
          if (editMode) {
            onToggle();
            return;
          }
          onClick();
        }}
      >
        <div className="persona-folder-card__main">
          <Folder size={40} fill="currentColor" strokeWidth={1.5} />
          <div>
            <strong>{folder.name || '폴더'}</strong>
            <div className="persona-folder-card__count">
              {avatarPersonas.length > 0 ? (
                <span
                  className={`persona-folder-card__avatars persona-folder-card__avatars--count-${avatarPersonas.length}`}
                  aria-hidden="true"
                >
                  {avatarPersonas.map((persona, index) => (
                    <span key={persona.id || index}>
                      {getPersonaImageUrl(persona) ? (
                        <img src={getPersonaImageUrl(persona)} alt="" />
                      ) : (
                        <em>{getPersonaInitial(persona.name)}</em>
                      )}
                    </span>
                  ))}
                </span>
              ) : null}
              {formatFolderPersonaCount(personaCount)}
            </div>
          </div>
        </div>
        {!editMode ? <ChevronRight size={18} strokeWidth={1.8} /> : null}
        {showFooterDate ? (
          <div className="persona-folder-card__footer">
            최근 수정 일자: {formatCompactDate(folder.updatedAt)}
          </div>
        ) : (
          <span className="persona-folder-card__footer-spacer" aria-hidden="true" />
        )}
      </button>
    </article>
  );
}

function PersonaCardSection({ title, body }) {
  return (
    <div className="persona-list-card__section">
      <strong>{title}</strong>
      <p>{body}</p>
    </div>
  );
}

function FolderModal({ value, creating, error, onChange, onSubmit, onCancel }) {
  return (
    <div className="persona-modal-backdrop" role="presentation">
      <form className="persona-folder-modal" onSubmit={onSubmit} onClick={(event) => event.stopPropagation()}>
        <h2>폴더명을 입력해주세요</h2>
        <input
          value={value}
          maxLength={FOLDER_NAME_MAX_LENGTH}
          onChange={(event) => onChange(event.target.value.slice(0, FOLDER_NAME_MAX_LENGTH))}
          placeholder="폴더명을 입력해주세요"
          autoFocus
        />
        <small>{error || `${value.length}/${FOLDER_NAME_MAX_LENGTH}`}</small>
        <div>
          <button type="button" onClick={onCancel} disabled={creating}>취소하기</button>
          <button type="submit" disabled={creating || !value.trim()}>폴더 생성</button>
        </div>
      </form>
    </div>
  );
}

function DeleteSelectionModal({ title, description, deleting, onCancel, onConfirm }) {
  return (
    <div className="persona-modal-backdrop" role="presentation">
      <section
        className="persona-delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="persona-delete-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="persona-delete-modal-title">{title}</h2>
        </header>
        <div className="persona-delete-modal__body">
          <p>{description}</p>
          <p>이 작업은 취소할 수 없습니다.</p>
        </div>
        <footer>
          <button type="button" onClick={onCancel} disabled={deleting}>
            취소하기
          </button>
          <button type="button" onClick={onConfirm} disabled={deleting}>
            {deleting ? '삭제 중' : '삭제하기'}
          </button>
        </footer>
      </section>
    </div>
  );
}

function MoveSelectionModal({ targets, selectedTarget, moving, onSelect, onCancel, onConfirm }) {
  const canConfirm = Boolean(selectedTarget) && !moving;

  return (
    <div className="persona-modal-backdrop" role="presentation">
      <section
        className="persona-move-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="persona-move-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="persona-move-modal-title">이동할 폴더를 선택해주세요.</h2>
        </header>
        <div className="persona-move-modal__body">
          <div className="persona-move-modal__list">
            {targets.map((target) => (
              <MoveTargetRow
                key={target.key}
                target={target}
                selected={selectedTarget === target.key}
                disabled={target.disabled || moving}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
        <footer>
          <button type="button" onClick={onCancel} disabled={moving}>
            취소하기
          </button>
          <button type="button" onClick={onConfirm} disabled={!canConfirm}>
            {moving ? '이동 중' : '이동하기'}
          </button>
        </footer>
      </section>
    </div>
  );
}

function MoveTargetRow({ target, selected, disabled, onSelect }) {
  return (
    <button
      type="button"
      className={[
        'persona-move-target',
        selected ? 'persona-move-target--selected' : '',
        disabled ? 'persona-move-target--disabled' : '',
      ].filter(Boolean).join(' ')}
      disabled={disabled}
      onClick={() => onSelect(target.key)}
    >
      <span className={`persona-move-target__icon persona-move-target__icon--${target.type}`}>
        {target.type === 'ungrouped' ? (
          <UserRound size={26} strokeWidth={1.8} />
        ) : (
          <Folder size={28} fill="currentColor" strokeWidth={1.5} />
        )}
      </span>
      <span className="persona-move-target__text">
        <strong>{target.name}</strong>
        <small>총 퍼소나 {target.count}</small>
      </span>
      {selected ? <span className="persona-move-target__check" aria-hidden="true" /> : null}
    </button>
  );
}

export default PersonaListPage;
